import { NgZone } from "@angular/core";
import { ActivatedRouteSnapshot } from "@angular/router";
import {
    QUERY_PARAMS,
    ITCITenantAppCategory,
    ITCITenantComponentOptions,
    ITCIApplication,
    ITCIAppDeploymentCategory,
    ITCIStatusFilterCategory,
    IConnVisibilityCategory,
    TCIMainMenu,
    TCITenantAppDetailsMenu,
    TCITenantEnvTools,
    TCITenantAppCategory,
    TCIAppDeploymentCategory,
    TCIStatusFilterCategory,
    TCIConnectionVisibility
} from "@tci/common";
import { Observable } from "rxjs";
import * as lodash from "lodash";

export function routeChildReduce(paramPart: ActivatedRouteSnapshot) {
    const fullParams = {};
    const params = paramPart.params || (<any>paramPart).value.params;
    lodash.assign(fullParams, params);
    paramPart.children.forEach(child => lodash.assign(fullParams, routeChildReduce(child)));
    return fullParams;
}

export function getAllMenuComponents(): ITCITenantComponentOptions<any>[] {
    const TCIMenuRequestedComp = [
        ...TCIMainMenu.getAllItems(),
        ...TCITenantAppDetailsMenu.getAllMenuItems(),
        ...TCITenantEnvTools.getAllItems()
    ];
    return TCIMenuRequestedComp;
}

/**
 * Tools for left-side category filter
 */
export interface ICategoryFilterOptions {
    category: ITCITenantAppCategory | ITCIAppDeploymentCategory | ITCIStatusFilterCategory;
    counter: number;
}

export function getCategoryFilterOptions(allLabel: string): ICategoryFilterOptions[] {
    return [
        { category: { id: allLabel, label: allLabel, iconUrl: null }, counter: 0 },
        ...TCITenantAppCategory.getAllCategories().map(category => ({ category, counter: 0 }))
    ];
}

export function getConnectionFilterOptions(allLabel: string): ICategoryFilterOptions[] {
    return [
        { category: { id: allLabel, label: allLabel, iconUrl: null }, counter: 0 },
        ...TCITenantAppCategory.getAllConnectCategories().map(category => ({ category, counter: 0 }))
    ];
}

export function getAppDeploymentFilterOptions(allLabel: string): ICategoryFilterOptions[] {
    return [
        { category: { id: allLabel, label: allLabel, iconUrl: null }, counter: 0 },
        ...TCIAppDeploymentCategory.getAllAppTypes().map(category => ({ category, counter: 0 }))
    ];
}

export function getAppStatusFilterOptions(allLabel: string): ICategoryFilterOptions[] {
    return [
        { category: { id: allLabel, label: allLabel, iconUrl: null }, counter: 0 },
        ...TCIStatusFilterCategory.getAllAppStatus().map(category => ({ category, counter: 0 }))
    ];
}

export interface IConnVisibilityFilterOptions {
    category: IConnVisibilityCategory;
    counter: number;
}

export function getVisibilityFilterOptions(allLabel: string): IConnVisibilityFilterOptions[] {
    return [
        { category: { id: allLabel, label: allLabel, iconUrl: null }, counter: 0 },
        ...TCIConnectionVisibility.getAllAppTypes()
            .filter(category => category.id !== TCIConnectionVisibility.UNKNOWN.id)
            .map(category => ({ category, counter: 0 }))
    ];
}

// App Details Route helper
export function getAppDetailsRoute(app: ITCIApplication): { route: string[], queryParams: Object } {
    const queryParams = {};
    queryParams[QUERY_PARAMS.APP_TYPE] = app.type.toString();

    return { route: ["details", QUERY_PARAMS.DEFAULT_ROUTE, app.id], queryParams };
}

export function setTimeoutInZone(ngZone: NgZone, callback: Function, delay: number = 0): void {
    ngZone.runOutsideAngular(() => {
        return Observable.of(true).delay(delay).do(() => ngZone.run(() => callback())).subscribe();
    });
}

export function timerInZone(ngZone: NgZone, callback: Function, delay: number = 0, interval = 100): Observable<number> {
    return Observable
        .timer(delay, interval)
        .do(next => ngZone.runOutsideAngular(() => {
            ngZone.run(
                () => callback()
            );
        }));
}

export function displayBigNumber(num: number): string {
    const si = [
        { value: 1, symbol: "" },
        { value: 1E3, symbol: "K" },
        { value: 1E6, symbol: "M" },
        { value: 1E9, symbol: "G" },
        { value: 1E12, symbol: "T" },
        { value: 1E15, symbol: "P" },
        { value: 1E18, symbol: "E" }
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    let i;
    for (i = si.length - 1; i > 0; i--) {
        if (num >= si[i].value) {
            break;
        }
    }
    return (num / si[i].value).toFixed(2).replace(rx, "$1") + si[i].symbol;
}
